// v3.99c.71 — FeedCard
// One card per Forge Feed entry. Visual treatment varies subtly by
// feed_type (badge color + emoji-free icon), but the structure stays
// uniform so the list reads cleanly.
import React from "react";
import { Link } from "react-router-dom";
import {
  Crown, Sprout, Sparkles, ShoppingBag, Gavel, Gift, BookOpen,
  Newspaper, MessageSquare, Network, Mountain, Megaphone, Star,
  ArrowRight, Pin,
} from "lucide-react";
import VoteControl from "./VoteControl";
import ShareButton from "../ShareButton";

const TYPE_META = {
  breeder_joined:      { label: "Breeder",         Icon: Sparkles,   color: "var(--gold)" },
  breeder_promoted:    { label: "Promotion",       Icon: Crown,      color: "var(--gold)" },
  strain_added:        { label: "Strain",          Icon: Network,    color: "var(--primary-text)" },
  lineage_updated:     { label: "Lineage",         Icon: Network,    color: "var(--primary-text)" },
  trait_featured:      { label: "Trait",           Icon: Mountain,   color: "var(--primary-text)" },
  family_featured:     { label: "Family",          Icon: Mountain,   color: "var(--primary-text)" },
  grow_log_created:    { label: "Grow Log",        Icon: Sprout,     color: "#4FA86F" },
  grow_log_milestone:  { label: "Grow Milestone",  Icon: Sprout,     color: "#4FA86F" },
  grow_log_photo:      { label: "Grow Photo",      Icon: Sprout,     color: "#4FA86F" },
  listing_created:     { label: "Listing",         Icon: ShoppingBag,color: "var(--gold)" },
  listing_featured:    { label: "Featured Listing",Icon: ShoppingBag,color: "var(--gold)" },
  auction_started:     { label: "Auction Live",    Icon: Gavel,      color: "#FF7A6A" },
  auction_ending:      { label: "Auction Ending",  Icon: Gavel,      color: "#FF7A6A" },
  auction_ended:       { label: "Auction Ended",   Icon: Gavel,      color: "var(--text-muted)" },
  giveaway_started:    { label: "Giveaway Live",   Icon: Gift,       color: "#FF7A6A" },
  giveaway_ending:     { label: "Giveaway Ending", Icon: Gift,       color: "#FF7A6A" },
  giveaway_ended:      { label: "Giveaway Ended",  Icon: Gift,       color: "var(--text-muted)" },
  giveaway_winner:     { label: "Winner",          Icon: Gift,       color: "var(--gold)" },
  article_published:   { label: "Article",         Icon: BookOpen,   color: "var(--primary-text)" },
  article_resurfaced:  { label: "From the Archive",Icon: BookOpen,   color: "var(--text-2)" },
  dispatch_published:  { label: "Dispatch",        Icon: Newspaper,  color: "var(--gold)" },
  review_posted:       { label: "Review",          Icon: Star,       color: "var(--gold)" },
  announcement:        { label: "Announcement",    Icon: Megaphone,  color: "var(--gold)" },
};
const FALLBACK_META = { label: "Update", Icon: MessageSquare, color: "var(--text-2)" };

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.max(0, (now - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
}

export default function FeedCard({ item, isAuthed = false, onAuthRequired }) {
  const meta = TYPE_META[item.feed_type] || FALLBACK_META;
  const { Icon } = meta;
  const href = item.cta_url || item.entity_url || "#";

  return (
    <article
      className="relative bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary-text)] transition-colors flex flex-col"
      style={{ borderRadius: 2 }}
      data-testid={`feed-card-${item.id}`}
    >
      {item.pinned && (
        <span className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em]"
              style={{ background: "rgba(212,175,55,0.18)", color: "var(--gold)",
                       border: "1px solid rgba(212,175,55,0.45)", borderRadius: 2 }}
              data-testid={`feed-card-pinned-${item.id}`}>
          <Pin className="w-3 h-3" /> Pinned
        </span>
      )}
      {item.is_promoted && (
        <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.18em]"
              style={{ background: "rgba(255,122,106,0.18)", color: "#FF7A6A",
                       border: "1px solid rgba(255,122,106,0.45)", borderRadius: 2 }}
              data-testid={`feed-card-promoted-${item.id}`}>
          {item.promotion_label || "Promoted"}
        </span>
      )}

      {/* v3.99c.76 — Per-type rich fallback. Strain cards show a SVG
          lineage mini-map; reviews render a screenshot-style card with
          stars + snippet; breeder/listing/giveaway items use their
          entity image with type-tinted gradient overlay; everything
          else uses the branded fallback below. Mobile-first: aspect
          ratio is 4:5 portrait on mobile (richer/Instagram-like) and
          1:1 square on desktop (denser/grid-friendly). */}
      {item.image_url ? (
        <Link to={href} className="block aspect-[4/5] sm:aspect-square bg-black overflow-hidden border-b border-[var(--border)] relative group">
          <img src={item.image_url} alt={item.title || meta.label}
               width="600" height="600"
               className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
               loading="lazy" decoding="async"
               onError={(e) => {
                 e.currentTarget.style.display = "none";
                 if (e.currentTarget.parentElement) {
                   e.currentTarget.parentElement.classList.add("feed-card-fallback");
                 }
               }} />
          {/* Subtle bottom gradient so the type chip stays readable
              when the image is light-coloured. */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none"
               style={{ background: "linear-gradient(to top, rgba(8,9,11,0.55), transparent)" }} />
        </Link>
      ) : (
        <FeedCardVisual meta={meta} item={item} href={href} />
      )}

      {/* Type chip + timestamp */}
      <div className="flex items-center justify-between px-3 pt-3">
        <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.18em]"
              style={{ color: meta.color }}>
          <Icon className="w-3 h-3" /> {meta.label}
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.16em]"
              style={{ color: "var(--text-muted)" }}>
          {formatTime(item.published_at)}
        </span>
      </div>

      {/* Title + body */}
      <Link to={href} className="block px-3 pt-2 pb-1 flex-1">
        <h3 className="font-display text-[15px] leading-tight mb-1 line-clamp-2"
            style={{ color: "var(--text)" }}>
          {item.title}
        </h3>
        {item.body && (
          <p className="text-[11px] leading-relaxed line-clamp-3"
             style={{ color: "var(--text-2)" }}>
            {item.body}
          </p>
        )}
      </Link>

      {/* Footer */}
      <div className="px-2 sm:px-3 py-2 sm:py-2.5 border-t border-[var(--border)] flex items-center gap-1.5 sm:gap-2 min-w-0">
        <VoteControl
          itemId={item.id}
          initialVote={item.my_vote || 0}
          initialScore={item.score || 0}
          isAuthed={isAuthed}
          onAuthRequired={onAuthRequired}
          testid={`feed-vote-${item.id}`}
          vertical={false}
        />
        <Link to={href}
              className="ml-auto inline-flex items-center gap-1 px-2 sm:px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] hover:bg-[var(--bg-2)] transition-colors min-w-0 truncate max-w-[110px] sm:max-w-none"
              style={{ borderColor: "var(--border-strong)", color: "var(--text-2)", borderRadius: 2 }}
              data-testid={`feed-card-cta-${item.id}`}>
          <span className="truncate">{item.cta_label || "View"}</span>
          <ArrowRight className="w-3 h-3 shrink-0" />
        </Link>
        <div className="hidden sm:block">
          <ShareButton
            title={item.title}
            description={item.body}
            path={item.cta_url || item.entity_url || "/forge-feed"}
            size="sm"
            variant="icon"
            testid={`feed-card-share-${item.id}`}
          />
        </div>
      </div>
    </article>
  );
}

// v3.99c.76 — Per-type rich visual router. Routes to a custom SVG
// composition when we can render something meaningful (strain →
// lineage mini-map; review → stars + snippet card; trait/family →
// genetics-grid pattern). Falls through to the generic branded card.
function FeedCardVisual({ meta, item, href }) {
  const t = item.feed_type || "";
  if (t === "strain_added" || t === "lineage_updated") {
    return <StrainLineageVisual meta={meta} item={item} href={href} />;
  }
  if (t === "review_posted") {
    return <ReviewVisual meta={meta} item={item} href={href} />;
  }
  if (t === "trait_featured" || t === "family_featured") {
    return <TraitFamilyVisual meta={meta} item={item} href={href} />;
  }
  if (t === "giveaway_winner") {
    return <WinnerVisual meta={meta} item={item} href={href} />;
  }
  if (t === "announcement") {
    return <AnnouncementVisual meta={meta} item={item} href={href} />;
  }
  return <FeedCardFallback meta={meta} title={item.title} href={href} />;
}

// Strain lineage mini-map — a stylised parent/child node diagram drawn
// purely in SVG. Uses the strain title + (when available) the parents
// from item.body or item.entity_meta. Always feels like *genetics*
// content rather than a generic gradient.
function StrainLineageVisual({ meta, item, href }) {
  const { Icon } = meta;
  const strainName = (item.title || "Strain").replace(/^.+? \u2014 /, "");
  // Best-effort parent extraction. Body format we use elsewhere:
  // "Lineage updated · Parent A × Parent B" — fall back gracefully.
  let parents = [];
  const body = item.body || "";
  const cross = body.match(/([A-Za-z0-9'.\- ]+)\s*[×x]\s*([A-Za-z0-9'.\- ]+)/);
  if (cross) parents = [cross[1].trim(), cross[2].trim()];
  return (
    <Link to={href} aria-label={strainName}
          data-testid="feed-card-strain-visual"
          className="block aspect-[4/5] sm:aspect-square overflow-hidden border-b border-[var(--border)] relative"
          style={{ background: "radial-gradient(circle at 30% 20%, #1a1d23 0%, #08090b 70%)" }}>
      <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full"
           preserveAspectRatio="xMidYMid meet">
        {/* faint gold grid */}
        <defs>
          <pattern id={`g-${item.id}`} width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#D4AF37" strokeOpacity="0.07" strokeWidth="0.5"/>
          </pattern>
        </defs>
        <rect width="200" height="200" fill={`url(#g-${item.id})`} />
        {/* parent nodes */}
        {parents.length === 2 ? (
          <>
            <line x1="60"  y1="60"  x2="100" y2="130" stroke="#4FA86F" strokeOpacity="0.6" strokeWidth="1.2"/>
            <line x1="140" y1="60"  x2="100" y2="130" stroke="#4FA86F" strokeOpacity="0.6" strokeWidth="1.2"/>
            <circle cx="60"  cy="60"  r="14" fill="#1a1d23" stroke="#4FA86F" strokeWidth="1.5"/>
            <circle cx="140" cy="60"  r="14" fill="#1a1d23" stroke="#4FA86F" strokeWidth="1.5"/>
            <text x="60"  y="44" textAnchor="middle" fontSize="7" fill="#9aa0a8"
                  fontFamily="ui-monospace, monospace">
              {parents[0].length > 14 ? parents[0].slice(0, 12) + "…" : parents[0]}
            </text>
            <text x="140" y="44" textAnchor="middle" fontSize="7" fill="#9aa0a8"
                  fontFamily="ui-monospace, monospace">
              {parents[1].length > 14 ? parents[1].slice(0, 12) + "…" : parents[1]}
            </text>
          </>
        ) : (
          // No parents known — single root node
          <circle cx="100" cy="60" r="14" fill="#1a1d23" stroke="#4FA86F" strokeWidth="1.5"/>
        )}
        {/* child node = this strain */}
        <circle cx="100" cy="130" r="22" fill="#D4AF37" fillOpacity="0.18"
                stroke="#D4AF37" strokeWidth="1.5"/>
        <text x="100" y="134" textAnchor="middle" fontSize="9" fill="#D4AF37"
              fontFamily="ui-monospace, monospace" fontWeight="600">GENETICS</text>
      </svg>
      <div className="absolute inset-x-3 bottom-3 text-center">
        <div className="font-mono text-[8px] uppercase tracking-[0.22em] mb-1"
             style={{ color: "var(--gold)", opacity: 0.8 }}>
          <Icon className="inline w-3 h-3 mr-1" /> Lineage
        </div>
        <div className="font-display text-[13px] leading-tight line-clamp-2"
             style={{ color: "var(--text)" }}>
          {strainName}
        </div>
      </div>
    </Link>
  );
}

// Review visual — screenshot-style card with star rating + snippet.
// Pulls the score from item.body when it contains "★★★★☆" / "5/5"
// patterns; otherwise renders 5 outlined stars.
function ReviewVisual({ meta, item, href }) {
  const body = item.body || "";
  // Extract a 1–5 rating from common patterns ("5/5", "4.5 stars", "★★★★")
  let rating = null;
  const fiveSlash = body.match(/(\d(?:\.\d)?)\s*\/\s*5/);
  if (fiveSlash) rating = Math.round(parseFloat(fiveSlash[1]));
  const stars = body.match(/★/g);
  if (rating == null && stars) rating = Math.min(5, stars.length);
  const r = rating == null ? 5 : Math.max(1, Math.min(5, rating));
  const snippet = body.replace(/[★☆]/g, "").replace(/^\s*[\d.]+\s*\/\s*5\s*[·\-—]?\s*/, "").trim();
  return (
    <Link to={href} aria-label={item.title}
          data-testid="feed-card-review-visual"
          className="block aspect-[4/5] sm:aspect-square overflow-hidden border-b border-[var(--border)] relative p-4 flex flex-col justify-between"
          style={{ background: "linear-gradient(160deg, #1a1612 0%, #0e0c0a 100%)" }}>
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] mb-2"
             style={{ color: "var(--gold)" }}>
          Verified Review
        </div>
        <div className="flex items-center gap-0.5 mb-3" aria-label={`${r} of 5 stars`}>
          {[1, 2, 3, 4, 5].map(n => (
            <svg key={n} viewBox="0 0 24 24" className="w-4 h-4"
                 fill={n <= r ? "#D4AF37" : "none"} stroke="#D4AF37" strokeWidth="1.5">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          ))}
        </div>
        <div className="font-display text-[14px] leading-tight line-clamp-2"
             style={{ color: "var(--text)" }}>
          {item.title}
        </div>
        {snippet && (
          <p className="mt-2 text-[11px] leading-relaxed line-clamp-4 italic"
             style={{ color: "var(--text-2)" }}>
            "{snippet.length > 140 ? snippet.slice(0, 138) + "…" : snippet}"
          </p>
        )}
      </div>
      <div className="font-mono text-[8px] uppercase tracking-[0.22em] self-end"
           style={{ color: "var(--text-muted)" }}>
        CannaForge · Forge Feed
      </div>
    </Link>
  );
}

// Trait / family visual — molecular dot grid that always feels like
// "genetics infrastructure" rather than a generic icon.
function TraitFamilyVisual({ meta, item, href }) {
  const { Icon } = meta;
  // Deterministic dot pattern from item.id so each trait gets a stable
  // unique look (avoids re-render flicker).
  const seed = (item.id || "x").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const dots = [];
  for (let r = 0; r < 7; r++) {
    for (let c = 0; c < 7; c++) {
      const v = (seed * (r + 1) * (c + 1)) % 19;
      if (v < 6) dots.push({ r, c, big: v < 2 });
    }
  }
  return (
    <Link to={href} aria-label={item.title}
          data-testid="feed-card-trait-visual"
          className="block aspect-[4/5] sm:aspect-square overflow-hidden border-b border-[var(--border)] relative"
          style={{ background: "linear-gradient(135deg, #0c1a14 0%, #08090b 70%)" }}>
      <svg viewBox="0 0 140 140" className="absolute inset-0 w-full h-full">
        {dots.map((d, i) => (
          <circle key={i} cx={20 + d.c * 16} cy={20 + d.r * 16}
                  r={d.big ? 4 : 2}
                  fill="#4FA86F" fillOpacity={d.big ? 0.85 : 0.35} />
        ))}
      </svg>
      <div className="absolute inset-x-3 bottom-3 text-center">
        <div className="font-mono text-[8px] uppercase tracking-[0.22em] mb-1"
             style={{ color: "#4FA86F", opacity: 0.85 }}>
          <Icon className="inline w-3 h-3 mr-1" /> {meta.label}
        </div>
        <div className="font-display text-[13px] leading-tight line-clamp-2"
             style={{ color: "var(--text)" }}>
          {item.title}
        </div>
      </div>
    </Link>
  );
}

function WinnerVisual({ meta, item, href }) {
  const { Icon } = meta;
  return (
    <Link to={href} aria-label={item.title}
          data-testid="feed-card-winner-visual"
          className="block aspect-[4/5] sm:aspect-square overflow-hidden border-b border-[var(--border)] relative p-5 flex flex-col items-center justify-center text-center"
          style={{ background: "radial-gradient(circle, #2a1c08 0%, #08090b 80%)" }}>
      <Icon className="w-12 h-12 mb-3" style={{ color: "#D4AF37" }} />
      <div className="font-mono text-[9px] uppercase tracking-[0.28em] mb-1"
           style={{ color: "var(--gold)" }}>Winner Announced</div>
      <div className="font-display text-[15px] leading-tight"
           style={{ color: "var(--text)" }}>{item.title}</div>
    </Link>
  );
}

function AnnouncementVisual({ meta, item, href }) {
  const { Icon } = meta;
  return (
    <Link to={href} aria-label={item.title}
          data-testid="feed-card-announcement-visual"
          className="block aspect-[4/5] sm:aspect-square overflow-hidden border-b border-[var(--border)] relative p-5 flex flex-col justify-center"
          style={{ background: "linear-gradient(160deg, #1c1408 0%, #08090b 100%)" }}>
      <div className="font-mono text-[9px] uppercase tracking-[0.28em] mb-2"
           style={{ color: "var(--gold)" }}>
        <Icon className="inline w-3 h-3 mr-1.5" /> From CannaForge
      </div>
      <div className="font-display text-[16px] leading-tight"
           style={{ color: "var(--text)" }}>{item.title}</div>
    </Link>
  );
}

// Branded CannaForge fallback for cards without an image. Uses the
// type meta's color + icon and a faux-noise/diagonal stripe so the
// card still feels intentional rather than empty.
function FeedCardFallback({ meta, title, href }) {
  const { Icon } = meta;
  return (
    <Link to={href}
          className="block aspect-[4/5] sm:aspect-square overflow-hidden border-b border-[var(--border)] relative"
          aria-label={title || meta.label}
          data-testid="feed-card-fallback"
          style={{
            background: `linear-gradient(135deg, var(--bg-2) 0%, var(--surface) 60%, var(--bg-2) 100%)`,
          }}>
      <div className="absolute inset-0 opacity-[0.08]"
           style={{
             backgroundImage:
               `repeating-linear-gradient(45deg, ${meta.color} 0 1px, transparent 1px 14px)`,
           }} />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
        <Icon className="w-12 h-12" style={{ color: meta.color, opacity: 0.55 }} />
        <span className="font-mono text-[9px] uppercase tracking-[0.22em]"
              style={{ color: meta.color, opacity: 0.6 }}>
          {meta.label}
        </span>
      </div>
      <div className="absolute bottom-2 right-2 font-mono text-[8px] uppercase tracking-[0.22em]"
           style={{ color: "var(--text-muted)", opacity: 0.5 }}>
        CannaForge
      </div>
    </Link>
  );
}
